import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { rem } from 'polished';
import styled from 'styled-components';

import { hubSpot } from 'api';
import {
  TOUR_GUIDE_CLIENT_PAGE_BUTTON,
  TOUR_GUIDE_HELP_CENTRE_BUTTON,
  TOUR_GUIDE_REQUEST_TECHNICAL_SUPPORT_BUTTON,
} from 'constants/tourGuide';
import { isEnabled, ServerSideFeature } from 'helpers/features';
import { breakpoints, misc } from 'helpers/styles/constants';
import { getActualYear, isNotBlank } from 'helpers/utils';
import { useLayoutData } from 'hooks/useLayoutData';
import routes from 'routes';
import { CLIENTS_PAGE_ROUTE } from 'routes/constants';
import { useGetPracticeSettings } from 'store/settings/selectors';
import {
  useGetIsCurrentUserAnAdviser,
  useGetIsCurrentUserAPracticeAdmin,
  useGetIsCurrentUserAPracticeDirector,
} from 'store/users/selectors';
import BaseFontSizeChanger from 'components/BaseFontSizeChanger/BaseFontSizeChanger';
import { DwsLogo } from 'components/DwsLogo/DwsLogo';

import { pagePadding } from '../styled';
import { FooterClickableElement, FooterDisabledElement } from './styled';

const Wrapper = styled.div<{ sidebarWidth: number }>`
  ${pagePadding};
  min-height: ${rem(misc.dashboardFooterHeight)};

  @media ${breakpoints.smUp} {
    margin-left: ${({ sidebarWidth }) => rem(sidebarWidth)};
  }
`;

const FooterItems = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-flow: column wrap;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSizes.dbSmall};
  line-height: 1.5;

  @media ${breakpoints.smUp} {
    flex-direction: row;
    align-items: center;
  }
`;

const FooterItemGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 0 ${rem(20)};
`;

const FooterLogoGroup = styled(FooterItemGroup)`
  flex-grow: 1;
  justify-content: flex-start;
`;

const PrivacyPolicyLink = styled.a`
  white-space: nowrap;
`;

const FooterItem = styled.div`
  margin-bottom: ${rem(10)};
  color: ${({ theme }) => theme.colors.textLight};

  a,
  button {
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }

  &:not(:last-child) {
    margin-right: ${rem(24)};
  }
`;

const CopyrightFooterItem = styled(FooterItem)`
  @media ${breakpoints.xsDown} {
    && {
      margin-right: auto;
    }
  }
`;

function Footer() {
  const { sidebarWidth } = useLayoutData(true);
  const { privacyPolicyUrl, authorizedRepresentativeName, referralScheme } =
    useGetPracticeSettings();
  const isCurrentUserAnAdviser = useGetIsCurrentUserAnAdviser();
  const isCurrentUserAPracticeAdmin = useGetIsCurrentUserAPracticeAdmin();
  const isCurrentUserAPracticeDirector = useGetIsCurrentUserAPracticeDirector();
  const location = useLocation();

  return (
    <Wrapper role="contentinfo" sidebarWidth={sidebarWidth}>
      <FooterItems>
        <FooterLogoGroup>
          <FooterItem>{isEnabled(ServerSideFeature.DwsLogo) && <DwsLogo />}</FooterItem>
        </FooterLogoGroup>

        <FooterItemGroup>
          <CopyrightFooterItem>
            <FormattedMessage
              id="shared.footer.copy"
              values={{ year: getActualYear(), authorizedRepresentativeName }}
            />
          </CopyrightFooterItem>

          {privacyPolicyUrl && (
            <FooterItem>
              <PrivacyPolicyLink target="_blank" rel="noopener noreferrer" href={privacyPolicyUrl}>
                <FormattedMessage id="onboarding.shared.privacyPolicy" />
              </PrivacyPolicyLink>
            </FooterItem>
          )}

          <FooterItem>
            <BaseFontSizeChanger />
          </FooterItem>
        </FooterItemGroup>
      </FooterItems>

      {/* We cannot do !isCurrentUserAClient because roleName can be empty
          in case of not confirmed user */}
      {isCurrentUserAnAdviser || isCurrentUserAPracticeAdmin || isCurrentUserAPracticeDirector ? (
        <FooterItems>
          <FooterItemGroup>
            <FooterItem id={TOUR_GUIDE_CLIENT_PAGE_BUTTON}>
              <FooterClickableElement
                href={CLIENTS_PAGE_ROUTE}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="shared.clientPage" />
              </FooterClickableElement>
            </FooterItem>

            <FooterItem id={TOUR_GUIDE_HELP_CENTRE_BUTTON}>
              <FooterClickableElement
                href={hubSpot().signIn().path}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="shared.helpCentre" />
              </FooterClickableElement>
            </FooterItem>

            <FooterItem id={TOUR_GUIDE_REQUEST_TECHNICAL_SUPPORT_BUTTON}>
              {location.pathname.includes('technical-support-form') ? (
                <FooterDisabledElement>
                  <FormattedMessage id="dashboard.shared.technicalSupport.title" />
                </FooterDisabledElement>
              ) : (
                <FooterClickableElement href={`#${routes.technicalSupportForm.reactPath()}`}>
                  <FormattedMessage id="dashboard.shared.technicalSupport.title" />
                </FooterClickableElement>
              )}
            </FooterItem>

            {isEnabled(ServerSideFeature.OurReferralScheme) && isNotBlank(referralScheme) && (
              <FooterItem>
                <FooterClickableElement
                  href={referralScheme}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id="shared.footer.referralScheme" />
                </FooterClickableElement>
              </FooterItem>
            )}
          </FooterItemGroup>
        </FooterItems>
      ) : null}
    </Wrapper>
  );
}

export default Footer;
